import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { CatalogEntry } from '../../services/inferenceApi';
import { formatPrice, formatTierPrice, isNew } from '../../utils/catalog';
import { captureFocus, restoreFocus, trapFocus } from '../../utils/focusTrap';
import ProviderBadge from './ProviderBadge';

type PrimaryCta = { label: string; onClick: () => void } | { label: string; to: string };

interface ModelDetailsModalProps {
  entry: CatalogEntry;
  currency: string;
  onClose: () => void;
  primaryCta: PrimaryCta;
  extraActions?: React.ReactNode;
}

const TITLE_ID = 'model-details-modal-title';

export default function ModelDetailsModal({ entry, currency, onClose, primaryCta, extraActions }: ModelDetailsModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const price = formatPrice(entry, currency);
  const hasAddedDate = !Number.isNaN(Date.parse(entry.created_at));

  // Keep the latest onClose in a ref so the keydown listener below can stay
  // current without depending on `onClose` directly - onClose is a fresh
  // inline closure on every parent render, and depending on it there would
  // tear down/re-run the capture-focus effect on any incidental re-render,
  // restoring focus to the trigger and re-capturing mid-open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Mount-only: capture the pre-open focus target and restore it on unmount.
  useEffect(() => {
    const previouslyFocused = captureFocus();
    panelRef.current?.focus();
    return () => restoreFocus(previouslyFocused);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        trapFocus(panelRef.current, e);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-[14px] border border-ps-strong bg-ps-card p-6 max-h-[85vh] overflow-y-auto shadow-xl shadow-black/60 focus:outline-none"
      >
        <div className="flex items-start gap-3">
          {entry.provider && (
            <ProviderBadge name={entry.provider} logoUrl={entry.provider_logo_url} size={40} />
          )}
          <div className="min-w-0 flex-1">
            {entry.provider && (
              <div className="text-xs text-gray-400 truncate">{entry.provider}</div>
            )}
            <h2 id={TITLE_ID} className="text-xl font-medium text-ps-text truncate">
              {entry.display_name}
            </h2>
          </div>
          {isNew(entry.created_at) && (
            <span className="shrink-0 ps-tag ps-tag-accent">
              new
            </span>
          )}
          <button
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 p-1 rounded-md text-ps-subtle hover:text-ps-text hover:bg-ps-raised"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        {(entry.category || entry.tags.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {entry.category && (
              <span className="ps-tag ps-tag-accent capitalize">
                {entry.category}
              </span>
            )}
            {entry.tags.map((t) => (
              <span key={t} className="ps-tag ps-tag-off">
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-4 rounded-[10px] border border-ps-border bg-ps-chrome p-4">
          <div>
            <div className="text-xs text-ps-subtle">Context</div>
            <div className="font-mono text-sm text-ps-text">{entry.context_length.toLocaleString()} tokens</div>
          </div>
          <div>
            <div className="text-xs text-ps-subtle">Parameters</div>
            <div className="font-mono text-sm text-ps-text">
              {entry.parameters_b > 0 ? `${entry.parameters_b}B` : '-'}
            </div>
          </div>
          {entry.price_tiers.length > 0 ? (
            <div className="col-span-2">
              <div className="text-xs text-ps-subtle mb-1">Price by resolution</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-ps-subtle">
                    <th className="text-left font-normal pb-1">Resolution</th>
                    <th className="text-right font-normal pb-1">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.price_tiers.map((t) => (
                    <tr key={t.key}>
                      <td className="py-0.5 font-mono text-ps-text">
                        {t.key}
                        {t.default && <span className="ml-1.5 text-xs text-ps-subtle">default</span>}
                      </td>
                      <td className="py-0.5 text-right font-mono text-ps-text">
                        {formatTierPrice(entry, t, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : price ? (
            price.output ? (
              <>
                <div>
                  <div className="text-xs text-ps-subtle">Input price</div>
                  <div className="font-mono text-sm text-ps-text">{price.input}</div>
                </div>
                <div>
                  <div className="text-xs text-ps-subtle">Output price</div>
                  <div className="font-mono text-sm text-ps-text">{price.output}</div>
                </div>
              </>
            ) : (
              <div>
                <div className="text-xs text-ps-subtle">Price</div>
                <div className="font-mono text-sm text-ps-text">{price.input}</div>
              </div>
            )
          ) : (
            <div className="col-span-2">
              <div className="font-mono text-sm text-ps-text">Pricing unavailable</div>
            </div>
          )}
          {hasAddedDate && (
            <div>
              <div className="text-xs text-ps-subtle">Added</div>
              <div className="font-mono text-sm text-ps-text">{new Date(entry.created_at).toLocaleDateString()}</div>
            </div>
          )}
        </div>

        {entry.description && (
          <p className="text-[13px] leading-relaxed text-ps-muted whitespace-pre-wrap mt-4">{entry.description}</p>
        )}

        <div className="mt-6 pt-4 border-t border-ps-border flex flex-wrap items-center gap-2">
          {'to' in primaryCta ? (
            <Link to={primaryCta.to} className="btn-primary btn-sm">
              {primaryCta.label}
            </Link>
          ) : (
            <button onClick={primaryCta.onClick} className="btn-primary btn-sm">
              {primaryCta.label}
            </button>
          )}
          {extraActions}
        </div>
      </div>
    </div>
  );
}
